import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageLayout } from "../components/PageLayout";
import { type Availability, api } from "../lib/api";
import { addDaysYmdJst, todayYmdJst } from "../lib/format";
import type { ReservationDraft, SeatSection } from "../types";

const SECTION_LABEL: Record<SeatSection, string> = {
  any: "席指定なし",
  counter: "カウンター",
  zashiki: "掘りごたつ",
};

export function ReservationNewPage() {
  const navigate = useNavigate();
  const shopQuery = useQuery({ queryKey: ["shop"], queryFn: api.shop });

  const [ymd, setYmd] = useState(todayYmdJst());
  const [partySize, setPartySize] = useState(2);
  const [seatSection, setSeatSection] = useState<SeatSection>("any");
  const [courseId, setCourseId] = useState<string | null>(null);
  const [time, setTime] = useState<string | null>(null);
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);

  const settings = shopQuery.data?.settings;
  const courses = settings?.courses ?? [];
  const maxParty = settings?.maxPartyForOnline ?? 8;
  const maxYmd = settings ? addDaysYmdJst(todayYmdJst(), settings.leadTimeMaxDays) : undefined;

  const availabilityQuery = useQuery({
    queryKey: ["availability", ymd, partySize, courseId],
    queryFn: () => api.availability({ date: ymd, partySize, courseId }),
    enabled: Boolean(settings) && partySize <= maxParty,
  });

  const filteredSlots = useMemo(
    () => filterSlotsBySection(availabilityQuery.data, seatSection),
    [availabilityQuery.data, seatSection],
  );

  const partyOptions = useMemo(() => Array.from({ length: maxParty }, (_, i) => i + 1), [maxParty]);

  function handleSubmit() {
    setSubmitError(null);
    if (!time) {
      setSubmitError("時間を選択してください");
      return;
    }
    if (!contactName.trim() || !contactPhone.trim()) {
      setSubmitError("お名前と電話番号は必須です");
      return;
    }
    const course = courses.find((c) => c.id === courseId) ?? null;
    const draft: ReservationDraft = {
      ymd,
      time,
      partySize,
      seatSection,
      courseId,
      courseLabel: course ? `${course.name}（¥${course.priceYen.toLocaleString()}）` : null,
      contactName: contactName.trim(),
      contactPhone: contactPhone.trim(),
      notes: notes.trim(),
    };
    navigate("/reservation/confirm", { state: { draft } });
  }

  if (shopQuery.isLoading) {
    return (
      <PageLayout title="ご予約">
        <p className="text-sm text-neutral-500">読み込み中…</p>
      </PageLayout>
    );
  }
  if (shopQuery.error || !settings) {
    return (
      <PageLayout title="ご予約">
        <p className="text-sm text-red-600">店舗情報を取得できませんでした。</p>
      </PageLayout>
    );
  }

  const partyTooLarge = partySize > maxParty;

  return (
    <PageLayout title="ご予約">
      <div className="flex flex-col gap-6 pb-24">
        <Field label="ご来店日">
          <input
            type="date"
            value={ymd}
            min={todayYmdJst()}
            max={maxYmd}
            onChange={(e) => {
              setYmd(e.target.value);
              setTime(null);
            }}
            className="w-full rounded-md border border-neutral-300 px-3 py-2 text-base"
          />
        </Field>

        <Field label="人数">
          <div className="grid grid-cols-4 gap-2">
            {partyOptions.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => {
                  setPartySize(n);
                  setTime(null);
                }}
                className={cn(
                  "rounded-md border px-3 py-2 text-sm",
                  partySize === n
                    ? "border-neutral-900 bg-neutral-900 text-white"
                    : "border-neutral-300 bg-white",
                )}
              >
                {n}名
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-neutral-500">
            {maxParty + 1}名以上は
            {shopQuery.data?.shop.phone ? `お電話（${shopQuery.data.shop.phone}）` : "お電話"}
            にて承ります。
          </p>
        </Field>

        <Field label="席種">
          <div className="grid grid-cols-3 gap-2">
            {(["any", "counter", "zashiki"] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  setSeatSection(s);
                  setTime(null);
                }}
                className={cn(
                  "rounded-md border px-3 py-2 text-sm",
                  seatSection === s
                    ? "border-neutral-900 bg-neutral-900 text-white"
                    : "border-neutral-300 bg-white",
                )}
              >
                {SECTION_LABEL[s]}
              </button>
            ))}
          </div>
        </Field>

        {courses.length > 0 ? (
          <Field label="コース（任意）">
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => setCourseId(null)}
                className={cn(
                  "rounded-md border px-3 py-2 text-left text-sm",
                  courseId === null
                    ? "border-neutral-900 bg-neutral-900 text-white"
                    : "border-neutral-300 bg-white",
                )}
              >
                コースなし（アラカルト）
              </button>
              {courses.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCourseId(c.id)}
                  className={cn(
                    "rounded-md border px-3 py-2 text-left text-sm",
                    courseId === c.id
                      ? "border-neutral-900 bg-neutral-900 text-white"
                      : "border-neutral-300 bg-white",
                  )}
                >
                  {c.name}　¥{c.priceYen.toLocaleString()}
                </button>
              ))}
            </div>
          </Field>
        ) : null}

        <Field label="ご来店時間">
          {partyTooLarge ? (
            <p className="text-sm text-red-600">人数が多すぎます。お電話にてご相談ください。</p>
          ) : availabilityQuery.isLoading ? (
            <p className="text-sm text-neutral-500">空席を確認中…</p>
          ) : availabilityQuery.data?.partyTooLarge ? (
            <p className="text-sm text-red-600">この人数のご予約はお電話にて承ります。</p>
          ) : filteredSlots.length === 0 ? (
            <p className="text-sm text-neutral-500">この条件で空きがありません。</p>
          ) : (
            <div className="grid grid-cols-4 gap-2">
              {filteredSlots.map((s) => (
                <button
                  key={s.time}
                  type="button"
                  onClick={() => setTime(s.time)}
                  className={cn(
                    "rounded-md border px-2 py-2 text-sm",
                    time === s.time
                      ? "border-neutral-900 bg-neutral-900 text-white"
                      : "border-neutral-300 bg-white",
                  )}
                >
                  {s.time}
                </button>
              ))}
            </div>
          )}
        </Field>

        <Field label="お名前">
          <input
            type="text"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            maxLength={50}
            placeholder="山田 太郎"
            className="w-full rounded-md border border-neutral-300 px-3 py-2 text-base"
          />
        </Field>

        <Field label="電話番号">
          <input
            type="tel"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            maxLength={30}
            placeholder="09012345678"
            className="w-full rounded-md border border-neutral-300 px-3 py-2 text-base"
          />
        </Field>

        <Field label="ご要望（任意）">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="アレルギーや席のご希望など"
            className="w-full rounded-md border border-neutral-300 px-3 py-2 text-base"
          />
        </Field>

        {submitError ? <p className="text-sm text-red-600">{submitError}</p> : null}

        <button
          type="button"
          onClick={handleSubmit}
          disabled={partyTooLarge}
          className="rounded-lg bg-neutral-900 px-4 py-3 text-sm font-semibold text-white disabled:bg-neutral-300"
        >
          確認画面へ
        </button>
      </div>
    </PageLayout>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-2 text-sm font-semibold">{label}</p>
      {children}
    </div>
  );
}

function cn(...xs: (string | false | null | undefined)[]) {
  return xs.filter(Boolean).join(" ");
}

function filterSlotsBySection(data: Availability | undefined, section: SeatSection) {
  if (!data) return [];
  return data.slots.filter((s) => s.sections[section]);
}
